import React, { useState, useEffect } from 'react';
  import { useSelector } from 'react-redux';
import { YStack, XStack, useTheme, Stack } from 'tamagui';
import { LinearGradient } from 'tamagui/linear-gradient';
import GroupIcon from '@mui/icons-material/Group';
import EventIcon from '@mui/icons-material/Event';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import { Group } from '@cobu/types';
import { Strings, URLS } from '../../constants';
import { CobuText, CobuImage, CobuStack } from '@cobu/ui';
import { RootState } from '../../store';

interface CobuGroupCardProps {
  group: Group;
  onPress: (groupId: string) => void;
  height?: number;
}

type GroupMetaWithCounts = {
  userIsMember?: boolean;
  groupMemberCount?: number;
  upcomingGroupEventCount?: number;
};

type GroupWithCounts = Omit<Group, 'meta'> & {
  createdAt?: string;
  meta?: GroupMetaWithCounts;
};

// Convert hex color to rgba with specified opacity
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CobuGroupCard: React.FC<CobuGroupCardProps> = ({
  group,
  onPress,
  height = 125, // Using token equivalent $31
}) => {
  const theme = useTheme();
  const buildingImage = useSelector((state: RootState) => state.auth.user?.building?.image);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const typedGroup = group as GroupWithCounts;

  const memberCount = typedGroup.meta?.groupMemberCount ?? 0;
  const eventCount = typedGroup.meta?.upcomingGroupEventCount ?? 0;
  
  // Responsive values
  const bannerPaddingX = isMobile ? '$2' : '$3';
  const bannerPaddingY = isMobile ? '$1' : '$2';
  const bannerBorderRadius = isMobile ? 12 : 20;
  const titleFontSize = isMobile ? '$3' : '$4';
  const iconSize = isMobile ? 12 : 14;
  
  // Get colorLink value and create rgba version with 0.4 opacity
  const colorLinkValue = theme.colorLink?.val || '#16a34a';
  const colorLinkWithOpacity = hexToRgba(colorLinkValue, 0.4);
  
  const gradientColors = [colorLinkValue, colorLinkWithOpacity];
  const handlePress = React.useCallback(() => {
    onPress(group.uuid);
  }, [group.uuid, onPress]);

  const getMemberText = (count: number) => {
    const template = count === 1
      ? Strings.get().groups.card.memberCount.singular
      : Strings.get().groups.card.memberCount.plural;
    return Strings.format(template, { count: count.toString() });
  };

  const getEventText = (count: number) => {
    const template = count === 1
      ? Strings.get().groups.card.eventCount.singular
      : Strings.get().groups.card.eventCount.plural;
    return Strings.format(template, { count: count.toString() });
  };

  const isNewGroup = () => {
    // if (!typedGroup?.createdAt) {
    //   return false;
    // }
    // const oneDayInMs = 24 * 60 * 60 * 1000;
    // const oneDayAgo = new Date(Date.now() - oneDayInMs);
    // return new Date(typedGroup.createdAt) > oneDayAgo;
    return false;
  };

  const maxLines = Math.max(1, Math.floor((height - 60) / 20));

  return (
    <YStack
      h={height}
      br="$3"
      ov="hidden"
      pos="relative"
      f={1}
    >
      <CobuStack
        f={1}
        onPress={handlePress}
      >
        <CobuImage
          source={{ uri: group.imageUrl || buildingImage || URLS.GROUP_PLACEHOLDER_IMAGE }}
          br="$3"
          w="100%"
          h="100%"
          ov="hidden"
        />

        <YStack
          pos="absolute"
          t={0}
          l={0}
          r={0}
          b={0}
          p="$3"
          jc="space-between"
        >
          {/* <LinearGradient
            colors={gradientColors}
            start={{ x: 0, y: 1 }}
            end={{ x: 1, y: 0 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          /> */}

          <YStack f={1} jc="space-between" zi="$1">
            {/* Pill-shaped banner with text - at top */}
            <CobuStack
              bg="rgba(255, 255, 255, 0.8)"
              br={bannerBorderRadius}
              px={bannerPaddingX}
              py={bannerPaddingY}
              alignSelf="flex-start"
              mt={isNewGroup() ? (isMobile ? '$6' : '$8') : 0}
            >
              <CobuText
                variant="heading"
                fontSize={titleFontSize}
                numberOfLines={maxLines}
                color="$black"
              >
                {group.name}
              </CobuText>
            </CobuStack>

            {/* Member and event counts - at bottom */}
            {(memberCount > 0 || eventCount > 0) && (
              <YStack gap="$2" alignSelf="flex-start">
                {memberCount > 0 && (
                  <CobuStack
                    bg="rgba(255, 255, 255, 0.8)"
                    br={bannerBorderRadius}
                    px={bannerPaddingX}
                    py={bannerPaddingY}
                    alignSelf="flex-start"
                    layout="horizontal"
                    ai="center"
                    gap={isMobile ? '$1' : '$2'}
                  >
                    <GroupIcon
                      width={iconSize}
                      height={iconSize}
                      style={{
                        color: "black",
                      }}
                    />
                    <CobuText
                      variant="caption"
                      color="$black"
                      fontSize={isMobile ? '$0' : undefined}
                    >
                      {getMemberText(memberCount)}
                    </CobuText>
                  </CobuStack>
                )}

                {eventCount > 0 && (
                  <CobuStack
                    bg="rgba(255, 255, 255, 0.8)"
                    br={bannerBorderRadius}
                    px={bannerPaddingX}
                    py={bannerPaddingY}
                    alignSelf="flex-start"
                    layout="horizontal"
                    ai="center"
                    gap={isMobile ? '$1' : '$2'}
                  >
                    <EventIcon
                      width={iconSize}
                      height={iconSize}
                      style={{
                        color: "black",
                      }}
                    />
                    <CobuText
                      variant="caption"
                      color="$black"
                      fontSize={isMobile ? '$0' : undefined}
                    >
                      {getEventText(eventCount)}
                    </CobuText>
                  </CobuStack>
                )}
              </YStack>
            )}
          </YStack>
        </YStack>
      </CobuStack>

      {isNewGroup() && (
        <YStack
          pos="absolute"
          t="$2"
          l="$2"
          bg="$cobuPink"
          px="$2"
          py="$1"
          br="$2"
          fd="row"
          ai="center"
          gap="$1"
          zi="$2"
        >
          {/* MaterialIcons fiber-new placeholder retained for native parity */}
          <FiberNewIcon sx={{ fontSize: 32, color: '#FFFFFF' }} />
          <CobuText
            variant="caption"
            color="$white"
          >
            {Strings.get().groups.card.newBadge}
          </CobuText>
        </YStack>
      )}
    </YStack>
  );
};

export default CobuGroupCard; 